import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Form,
  Input,
  Message,
  Typography,
} from '@arco-design/web-react';
import { IconLock, IconUser } from '@arco-design/web-react/icon';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authApi, publicApi, setAccessToken } from '@/api';
import { useAuthStore } from '@/store/authStore';
import { EMAIL_REGEX, PHONE_INTL_REGEX } from '@/utils/validators';
import type { RegisterRequest } from '@/types/user';

type InviteStatus = 'idle' | 'checking' | 'valid' | 'invalid';

export default function RegisterView() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form] = Form.useForm<RegisterRequest & { confirmPassword: string }>();
  const [loading, setLoading] = useState(false);
  const [inviteStatus, setInviteStatus] = useState<InviteStatus>('idle');
  const [inviterName, setInviterName] = useState('');
  const [inviterPhone, setInviterPhone] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const validateInvite = useCallback(async (code: string) => {
    const trimmed = code.trim();
    if (!trimmed) {
      setInviteStatus('idle');
      setInviterName('');
      setInviterPhone('');
      return;
    }
    setInviteStatus('checking');
    try {
      const result = await publicApi.validateInvitation(trimmed);
      if (result.valid) {
        setInviteStatus('valid');
        setInviterName(result.inviterDisplayName ?? '');
        setInviterPhone(result.inviterPhone ?? '');
      } else {
        setInviteStatus('invalid');
        setInviterName('');
        setInviterPhone('');
      }
    } catch {
      setInviteStatus('invalid');
    }
  }, []);

  useEffect(() => {
    const invite = searchParams.get('invite');
    if (invite) {
      form.setFieldValue('inviteCode', invite);
      void validateInvite(invite);
    }
  }, [searchParams, form, validateInvite]);

  const onInviteChange = (value: string) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => void validateInvite(value), 500);
  };

  const canSubmit =
    inviteStatus === 'valid' && !loading && Boolean(form.getFieldValue('inviteCode')?.trim());

  const onSubmit = async () => {
    if (loading || inviteStatus !== 'valid') return;
    try {
      const values = await form.validate();
      if (values.password !== values.confirmPassword) {
        Message.error('两次输入的密码不一致');
        return;
      }
      setLoading(true);
      const result = await authApi.register({
        inviteCode: values.inviteCode.trim(),
        username: values.username,
        email: values.email,
        phone: values.phone.trim(),
        password: values.password,
        displayName: values.displayName || undefined,
      });
      setAccessToken(result.accessToken);
      useAuthStore.setState({ profile: result.profile, authenticated: true });
      Message.success('注册成功');
      navigate('/dashboard');
    } catch (e) {
      if (e instanceof Error && !(e as { isBusinessError?: boolean }).isBusinessError) {
        Message.error(e.message);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #0f1f48 0%, #165DFF 100%)',
        padding: 24,
      }}
    >
      <Card
        style={{
          width: 'min(560px, 100%)',
          borderRadius: 16,
          boxShadow: '0 16px 48px rgba(0,0,0,0.22)',
        }}
        bodyStyle={{ padding: '40px 48px 48px' }}
      >
        <Typography.Title heading={3} style={{ marginTop: 0, marginBottom: 8 }}>
          注册 UP·CARD 账号
        </Typography.Title>
        <Typography.Text type="secondary">
          须持有有效邀请码方可注册
        </Typography.Text>

        {inviteStatus === 'valid' && inviterName ? (
          <Alert
            type="success"
            style={{ marginTop: 16 }}
            content={`${inviterName}${inviterPhone ? ` · ${inviterPhone}` : ''} 邀请您注册`}
          />
        ) : null}
        {inviteStatus === 'invalid' ? (
          <Alert type="error" style={{ marginTop: 16 }} content="邀请码无效或已失效" />
        ) : null}
        {inviteStatus === 'idle' && !form.getFieldValue('inviteCode') ? (
          <Alert type="warning" style={{ marginTop: 16 }} content="请输入邀请码" />
        ) : null}

        <Form form={form} layout="vertical" style={{ marginTop: 24 }} size="large">
          <Form.Item
            label="邀请码"
            field="inviteCode"
            rules={[{ required: true, message: '请输入邀请码' }]}
          >
            <Input placeholder="inv_xxxx" onChange={onInviteChange} allowClear />
          </Form.Item>
          <Form.Item
            label="登录名称"
            field="username"
            rules={[
              { required: true, message: '请输入登录名称' },
              { minLength: 3, message: '至少 3 位' },
              { match: /^[A-Za-z0-9_]+$/, message: '仅允许字母、数字、下划线' },
            ]}
          >
            <Input prefix={<IconUser />} placeholder="登录名称" allowClear />
          </Form.Item>
          <Form.Item
            label="邮箱"
            field="email"
            rules={[
              { required: true, message: '请输入邮箱' },
              { match: EMAIL_REGEX, message: '邮箱格式不正确' },
            ]}
          >
            <Input placeholder="user@example.com" />
          </Form.Item>
          <Form.Item
            label="电话"
            field="phone"
            rules={[
              { required: true, message: '请输入电话' },
              { match: PHONE_INTL_REGEX, message: '请使用 +区号 开头格式' },
            ]}
          >
            <Input placeholder="+86-138-0000-0000" allowClear />
          </Form.Item>
          <Form.Item label="显示名（可选）" field="displayName">
            <Input placeholder="如：华东区代理" maxLength={64} />
          </Form.Item>
          <Form.Item
            label="登录密码"
            field="password"
            rules={[
              { required: true, message: '请输入密码' },
              { minLength: 8, message: '至少 8 位' },
            ]}
          >
            <Input.Password prefix={<IconLock />} placeholder="至少 8 位，含字母与数字" />
          </Form.Item>
          <Form.Item
            label="确认密码"
            field="confirmPassword"
            rules={[{ required: true, message: '请再次输入密码' }]}
          >
            <Input.Password placeholder="再次输入密码" />
          </Form.Item>
          <Button
            type="primary"
            long
            loading={loading}
            disabled={!canSubmit}
            onClick={() => void onSubmit()}
            style={{ height: 44 }}
          >
            注册并登录
          </Button>
        </Form>

        <div style={{ marginTop: 20, textAlign: 'center' }}>
          <Typography.Text type="secondary">
            已有账号？<Link to="/login">去登录</Link>
          </Typography.Text>
        </div>
      </Card>
    </div>
  );
}
